import React, { useMemo } from 'react';
import { Modal, Spinner, Table, Badge } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import useTradesForDate from '../../hooks/useTradesForDate';

const fmtTime = (str) => {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
    });
  } catch (_) {
    return '—';
  }
};

const fmtPrice = (v) => (v != null && Number.isFinite(Number(v)) ? `$${Number(v).toFixed(2)}` : '—');

/**
 * Lists positions opened on an ET calendar day — same source as Max Trades / Snapshot "Trades".
 */
export default function TradesUsedTodayModal({
  show,
  onHide,
  account,
  tradeDate,
  tradesTaken = null,
  maxTrades = null,
}) {
  const { colors } = useTheme();
  const { trades, total, loading, error } = useTradesForDate(
    show ? account : null,
    tradeDate,
    100
  );

  const headerCount = tradesTaken != null ? tradesTaken : total;
  const limitLabel = maxTrades != null && maxTrades > 0 ? ` / ${maxTrades} limit` : '';

  const sorted = useMemo(
    () => [...(trades || [])].sort((a, b) => {
      const ta = new Date(a.trade_timestamp || a.opened_at || 0).getTime();
      const tb = new Date(b.trade_timestamp || b.opened_at || 0).getTime();
      return tb - ta;
    }),
    [trades]
  );

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header
        closeButton
        closeVariant={colors.text === '#fff' || colors.mode === 'dark' ? 'white' : undefined}
        style={{ backgroundColor: colors.bgCard, borderColor: colors.border, color: colors.text }}
      >
        <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 650 }}>
          Trades counting toward today&apos;s limit
          <span style={{ display: 'block', marginTop: 4, fontSize: '0.8rem', fontWeight: 500, color: colors.textMuted }}>
            {headerCount} opened today (ET){limitLabel}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: colors.bgCard, color: colors.text }}>
        <p style={{ fontSize: '0.84rem', color: colors.textLight, marginBottom: 14, lineHeight: 1.45 }}>
          Max Trades counts each <strong style={{ color: colors.text }}>position opened</strong> today
          in Eastern Time — including still-open positions. Performance &quot;Today&quot; only counts
          <strong style={{ color: colors.text }}> closed</strong> trades, so the two numbers can differ.
        </p>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 28 }}>
            <Spinner animation="border" style={{ color: colors.primary }} />
          </div>
        ) : error ? (
          <div style={{ color: colors.danger, fontSize: '0.875rem' }}>{error}</div>
        ) : sorted.length === 0 ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem', padding: '12px 0' }}>
            No positions opened today (ET).
          </div>
        ) : (
          <div style={{ maxHeight: 'min(60vh, 420px)', overflowY: 'auto' }}>
            <Table
              hover
              size="sm"
              style={{
                fontSize: '0.82rem',
                marginBottom: 0,
                backgroundColor: colors.bgPrimary,
                color: colors.text,
                '--bs-table-bg': colors.bgPrimary,
                '--bs-table-color': colors.text,
                '--bs-table-hover-bg': colors.bgSecondary,
                '--bs-table-hover-color': colors.text,
                '--bs-table-border-color': colors.border,
              }}
            >
              <thead>
                <tr style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ color: colors.textMuted }}>#</th>
                  <th style={{ color: colors.textMuted }}>Symbol</th>
                  <th style={{ color: colors.textMuted }}>Side</th>
                  <th style={{ color: colors.textMuted }}>Opened (ET)</th>
                  <th style={{ color: colors.textMuted }}>Qty</th>
                  <th style={{ color: colors.textMuted }}>Entry</th>
                  <th style={{ color: colors.textMuted }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t, i) => {
                  const status = (t.status || (t.closed_at ? 'closed' : 'open')).toLowerCase();
                  const isOpen = status === 'open';
                  return (
                    <tr key={t.trade_id || `${t.symbol}-${i}`}>
                      <td style={{ color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                        {sorted.length - i}
                      </td>
                      <td style={{ fontWeight: 700 }}>{t.symbol}</td>
                      <td style={{ textTransform: 'capitalize' }}>{t.direction || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(t.trade_timestamp || t.opened_at)}</td>
                      <td>{t.quantity ?? '—'}</td>
                      <td>{fmtPrice(t.avg_entry_price ?? t.entry_price)}</td>
                      <td>
                        <Badge
                          bg={isOpen ? 'primary' : 'secondary'}
                          style={{
                            fontWeight: 600,
                            fontSize: '0.68rem',
                            backgroundColor: isOpen ? colors.primary : colors.textMuted,
                          }}
                        >
                          {isOpen ? 'OPEN' : 'CLOSED'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
